import { useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Calendar, Gift, Phone } from 'lucide-react';
import { businessInfo } from '../data/menuData';

// Animation variants defined outside component
const fadeInUpVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 }
};

const transitionConfig = { duration: 0.6 };

// Specials data defined outside component
const specialsData = [
  {
    id: 1,
    title: "Wedding Cakes",
    dates: 'Book in advance',
    description: 'Make your special day unforgettable with our stunning custom wedding cakes. From elegant tiers to modern designs, we create the cake of your dreams.',
    icon: Heart,
    color: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/hxyd2guo_1.PNG'
  },
  {
    id: 2,
    title: 'Friday Cake Offer',
    dates: 'Every Friday',
    description: 'Start your weekend with our special Friday cake deal - a whole homemade chocolate cake for just TZS 10,000!',
    icon: Calendar,
    color: 'bg-bakery-accent dark:bg-amber-900/30',
    iconColor: 'text-bakery-primary',
    image: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/izqnsltc_MKD%20Chocolate%20Cake%20Poster.png'
  },
  {
    id: 3,
    title: 'Custom Birthday Cakes',
    dates: 'Order in advance',
    description: 'Let us create the perfect birthday cake for your loved ones. Custom designs and flavors available.',
    icon: Gift,
    color: 'bg-bakery-secondary/30 dark:bg-green-900/30',
    iconColor: 'text-green-600',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/1fi0lhaq_3.webp'
  }
];

// Special Card Component
const SpecialCard = ({ special, index, isInView }) => {
  const transition = useMemo(() => ({ 
    duration: 0.6, 
    delay: index * 0.15 
  }), [index]);

  return (
    <motion.div
      variants={fadeInUpVariants}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
      transition={transition}
      className="bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
      data-testid={`special-card-${special.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={special.image}
          alt={special.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        <div className={`absolute top-4 right-4 ${special.color} p-3 rounded-full`}>
          <special.icon size={24} className={special.iconColor} />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-bakery-primary font-medium mb-2">
          <Calendar size={16} />
          {special.dates}
        </div>
        <h3 className="font-heading text-2xl font-bold text-bakery-text dark:text-bakery-bg mb-3">
          {special.title}
        </h3>
        <p className="text-bakery-text/70 dark:text-bakery-bg/70 mb-6">
          {special.description}
        </p>
        <a
          href={`tel:${businessInfo.phone}`}
          className="flex items-center justify-center gap-2 bg-bakery-primary text-white py-3 rounded-full font-medium hover:bg-bakery-primary/90 transition-colors duration-300"
          data-testid={`special-order-btn-${special.id}`}
        >
          <Phone size={18} />
          Order Now
        </a>
      </div>
    </motion.div>
  );
};

const SpecialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const bannerTransition = useMemo(() => ({ duration: 0.6, delay: 0.5 }), []);

  return (
    <section id="specials" className="py-20 md:py-32 bg-bakery-muted dark:bg-gray-800" data-testid="specials-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          variants={fadeInUpVariants}
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={transitionConfig}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-bakery-primary">Don't Miss Out</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2">
            Special <span className="text-bakery-primary">Offers</span>
          </h2>
          <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-4 max-w-2xl mx-auto">
            Check out our seasonal specials and limited-time offers
          </p>
        </motion.div>

        {/* Specials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {specialsData.map((special, index) => (
            <SpecialCard 
              key={special.id} 
              special={special} 
              index={index} 
              isInView={isInView} 
            />
          ))}
        </div>

        {/* Promotional banner */}
        <motion.div
          variants={fadeInUpVariants}
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={bannerTransition}
          className="mt-16 bg-bakery-text dark:bg-gray-900 rounded-3xl p-8 md:p-12 text-center"
        >
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-bakery-bg mb-4">
            Planning an Event?
          </h3>
          <p className="text-bakery-bg/80 text-lg mb-6 max-w-2xl mx-auto">
            Let us cater your next celebration! From weddings to corporate events, 
            we offer bulk orders and custom packages.
          </p>
          <a
            href={`tel:${businessInfo.phone}`}
            className="inline-flex items-center gap-2 bg-bakery-primary text-white px-8 py-4 rounded-full font-medium btn-lift text-lg"
            data-testid="event-inquiry-btn"
          >
            <Phone size={20} />
            Inquire Now
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SpecialsSection;
